import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { getAllPaymentReceipts, createPaymentReceipt, updatePaymentReceipt, getAllEngagements } from '../../store/actions';
import { GenCustomTable } from '../../components/customComponents/genCustomTable/GenCustomTable';
import { Plus, CheckCircle, XCircle, Clock } from 'lucide-react';
import Header from '../../components/layout/Header';
import Badge, { statusToBadge } from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import PaymentReceiptForm, { STEP_LABELS } from '../../components/forms/PaymentReceiptForm';
import { PAYMENT_MODE, RECEIPT_STATUS, formatDate, formatINR, label, options, today } from '../../constants/garmentCodes';

const STATUS = { SUBMITTED: 2, UNDER_REVIEW: 3, NEEDS_INFO: 4, APPROVED: 5, POSTED: 6, REJECTED: 7 };
const statusIcon = {
  [STATUS.POSTED]: <CheckCircle size={13} className="text-[#1A6B3E]" />,
  [STATUS.APPROVED]: <CheckCircle size={13} className="text-[#1A6B3E]" />,
  [STATUS.REJECTED]: <XCircle size={13} className="text-[#C0222B]" />,
};
const SUMMARY = [
  { key: 'posted', label: 'Posted to Wallet', match: [STATUS.POSTED, STATUS.APPROVED], color: 'text-[#1A6B3E]', bg: 'bg-[#E8F5EE]', border: 'border-[#A8DEC0]' },
  { key: 'review', label: 'Under Review', match: [STATUS.SUBMITTED, STATUS.UNDER_REVIEW], color: 'text-[#92560C]', bg: 'bg-[#FEF3E2]', border: 'border-[#F7C97A]' },
  { key: 'info', label: 'Needs Information', match: [STATUS.NEEDS_INFO], color: 'text-[#3D52A0]', bg: 'bg-[#EEF0FB]', border: 'border-[#B8C1E8]' },
  { key: 'rejected', label: 'Rejected', match: [STATUS.REJECTED], color: 'text-[#C0222B]', bg: 'bg-[#FDECEA]', border: 'border-[#F5A9AC]' },
];

export default function BrandReceipts() {
  const dispatch = useDispatch();
  const [params] = useSearchParams();
  const engagementId = params.get('engagementId') || '';
  const [showCreate, setShowCreate] = useState(false);
  const receipts = useSelector((state) => state.paymentreceipt.receipts);
  const loading = useSelector((state) => state.paymentreceipt.loading);
  const engagements = useSelector((state) => state.engagement.engagements);
  const userId = useSelector((state) => state.auth?.loginUser?.userId ?? null);
  const brandId = useSelector((state) => state.brand.profile?.id);
  const engagementMap = useMemo(() => Object.fromEntries(engagements.map((e) => [String(e.id), e])), [engagements]);

  useEffect(() => {
    dispatch(getAllEngagements({ brandId }));
  }, [dispatch, brandId]);

  useEffect(() => {
    dispatch(getAllPaymentReceipts({ brandId, engagementId, status: '' }));
  }, [dispatch, brandId, engagementId]);

  const counts = receipts.reduce((acc, r) => {
    SUMMARY.forEach((s) => {
      if (s.match.includes(r.status)) acc[s.key] = (acc[s.key] || 0) + 1;
    });
    return acc;
  }, {});

  const submit = (body) => {
    dispatch(createPaymentReceipt({ payload: body }));
    setShowCreate(false);
  };

  const resubmit = (r) => dispatch(updatePaymentReceipt({ payload: { id: r.id, status: STATUS.SUBMITTED, rejectionReason: null, submittedDate: today() } }));

  const [receiptStep, setReceiptStep] = useState(1);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Header
        title="Payment Receipts"
        subtitle={`${receipts.length} receipts`}
        actions={<Button variant="primary" size="sm" icon={<Plus size={14} />} onClick={() => setShowCreate(true)}>Submit receipt</Button>}
      />

      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {SUMMARY.map((s) => (
            <div key={s.key} className={`rounded-xl border p-4 ${s.bg} ${s.border}`}>
              <p className={`text-2xl font-bold ${s.color}`} style={{ fontFamily: 'Manrope, sans-serif' }}>{counts[s.key] || 0}</p>
              <p className={`text-xs font-medium mt-1 ${s.color}`}>{s.label}</p>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3">
          {loading && <span className="text-xs text-[#9B9893]">Loading…</span>}
        </div>

        <div className="bg-white rounded-xl border border-[#E5E3DE] overflow-hidden [&_.MuiTablePagination-toolbar]:!justify-end [&_.MuiTablePagination-spacer]:!flex-1 [&_.MuiTablePagination-spacer]:!block [&_.MuiTablePagination-selectLabel]:!m-0 [&_.MuiTablePagination-displayedRows]:!m-0 [&_.MuiTablePagination-root]:!text-[#5C5A54]">
          <GenCustomTable
            isExportEnabled={true}
            exportFileName="Payment Receipts"
            columns={[
              { id: 'receiptId', label: 'RECEIPT ID', align: 'left' },
              { id: 'manufacturer', label: 'MANUFACTURER', align: 'left' },
              { id: 'amount', label: 'AMOUNT', align: 'left' },
              { id: 'paymentDate', label: 'PAYMENT DATE', align: 'left' },
              { id: 'mode', label: 'MODE', align: 'left' },
              { id: 'transactionId', label: 'TRANSACTION ID', align: 'left' },
              { id: 'status', label: 'STATUS', align: 'left' },
              { id: 'reason', label: 'NOTE', align: 'left' },
              { id: 'action', label: '', align: 'center', disableFilter: true },
            ]}
            rows={receipts.map((r) => {
              const eng = engagementMap[String(r.engagementId)];
              const status = label(RECEIPT_STATUS, r.status);
              return {
                id: r.id,
                rColumns: [
                  <div key="id" label={r.receiptNumber || ''} className={`flex items-center w-full h-full px-2 py-1 font-mono text-xs text-[#3D52A0] font-semibold ${r.__optimistic ? 'opacity-60' : ''}`}>{r.receiptNumber || `#${r.id}`}</div>,
                  <div key="mfg" label={eng?.manufacturerName || ''} className="flex flex-col justify-center items-start w-full h-full px-2 py-1">
                    <p className="text-sm font-medium text-[#1A1915]">{eng?.manufacturerName || '—'}</p>
                    <p className="text-xs text-[#9B9893]">{eng?.name || ''}</p>
                  </div>,
                  <div key="amt" label={formatINR(r.amount)} className="flex items-center w-full h-full px-2 py-1 font-semibold text-[#1A1915]">{formatINR(r.amount)}</div>,
                  <div key="date" label={r.paymentDate || ''} className="flex items-center w-full h-full px-2 py-1 text-xs text-[#5C5A54]">{formatDate(r.paymentDate)}</div>,
                  <div key="mode" label={label(PAYMENT_MODE, r.mode)} className="flex items-center w-full h-full px-2 py-1 text-xs text-[#5C5A54]">{label(PAYMENT_MODE, r.mode)}</div>,
                  <div key="txn" label={r.transactionId || r.utr || ''} className="flex items-center w-full h-full px-2 py-1 font-mono text-xs text-[#9B9893]">{r.transactionId || r.utr || '—'}</div>,
                  <div key="stat" label={status} className="flex items-center w-full h-full px-2 py-1">
                    <div className="flex items-center gap-1.5">
                      {statusIcon[r.status] || <Clock size={13} className="text-[#92560C]" />}
                      <Badge variant={statusToBadge(status)}>{status}</Badge>
                    </div>
                  </div>,
                  <div key="reason" label={r.rejectionReason || ''} className="flex items-center w-full h-full px-2 py-1 text-xs text-[#C0222B] max-w-[220px] truncate">{r.rejectionReason || ''}</div>,
                  <div key="act" label="" className="flex items-center justify-center w-full h-full px-2 py-1">
                    {(r.status === STATUS.REJECTED || r.status === STATUS.NEEDS_INFO) && <Button variant="outline" size="xs" onClick={() => resubmit(r)}>Resubmit</Button>}
                  </div>,
                ],
              };
            })}
          />
          {!loading && receipts.length === 0 && <p className="py-10 text-center text-sm text-[#9B9893]">No receipts submitted yet</p>}
        </div>
      </div>

      <Modal
        open={showCreate}
        onClose={() => { setShowCreate(false); setReceiptStep(1); }}
        title="Submit Payment Receipt"
        subtitle={`Step ${receiptStep} of 4 — ${STEP_LABELS[receiptStep - 1]}`}
        size="lg"
      >
        <PaymentReceiptForm
          engagements={engagements}
          defaultEngagementId={engagementId}
          userId={userId}
          onSubmit={submit}
          onCancel={() => setShowCreate(false)}
          onStepChange={setReceiptStep}
        />
      </Modal>
    </div>
  );
}
